import { BookOpen, Quotes } from "@phosphor-icons/react";

// Renderiza markdown-lite (headings, listas, blockquote, bold, italic, code inline)
// sem depender de biblioteca externa. Escapa HTML por padrão.
function escapeHtml(s) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
function inline(raw) {
  // Ordem importa: código > bold > italic > destaque "Capítulo N"
  let s = escapeHtml(raw);
  s = s.replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 rounded bg-[#EEF2FF] text-[#1E5FFF] text-[0.9em] font-mono-num">$1</code>');
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-bold text-[#12143A]">$1</strong>');
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em class="text-[#12143A]/90">$2</em>');
  s = s.replace(/(Cap[ií]tulo\s+\d+|Capítulo\s+[A-ZÀ-Ú][^.,\n]{1,40})/g, '<span class="px-1.5 py-0.5 rounded bg-[#FFF6EA] text-[#B45309] font-semibold text-[0.9em]">$1</span>');
  return s;
}

function renderItem(it) {
  if (typeof it === "string") return <span dangerouslySetInnerHTML={{ __html: inline(it) }} />;
  return (
    <>
      <span dangerouslySetInnerHTML={{ __html: inline(it.text) }} />
      {it.sub && it.sub.length > 0 && (
        <ul className="mt-1.5 ml-1 space-y-1">
          {it.sub.map((s, i) => (
            <li key={i} className="flex gap-2 items-start leading-relaxed text-[14px]">
              <span className="mt-2 w-1 h-1 rounded-full bg-zinc-400 flex-shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: inline(s) }} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function Block({ node }) {
  if (node.type === "hr") return <hr className="my-4 border-0 h-px bg-gradient-to-r from-transparent via-zinc-200 to-transparent" />;
  if (node.type === "h1") return <h3 className="font-heading text-xl font-black text-[#12143A] mt-4 mb-2 flex items-center gap-2"><span className="w-1 h-6 rounded-full gradient-primary flex-shrink-0" /><span dangerouslySetInnerHTML={{ __html: inline(node.text) }} /></h3>;
  if (node.type === "h2") return <h4 className="font-heading text-lg font-black text-[#12143A] mt-3 mb-1.5" dangerouslySetInnerHTML={{ __html: inline(node.text) }} />;
  if (node.type === "h3") return <h5 className="font-heading text-base font-bold text-[#1E5FFF] mt-2 mb-1 uppercase tracking-wide" dangerouslySetInnerHTML={{ __html: inline(node.text) }} />;
  if (node.type === "quote") return (
    <blockquote className="my-3 pl-4 pr-3 py-2 border-l-4 border-[#1E5FFF] bg-[#F4F8FF] rounded-r-2xl flex gap-2">
      <Quotes size={16} weight="fill" className="text-[#1E5FFF] flex-shrink-0 mt-1" />
      <span dangerouslySetInnerHTML={{ __html: inline(node.text) }} />
    </blockquote>
  );
  if (node.type === "ul") return (
    <ul className="my-2 space-y-1.5">
      {node.items.map((it, i) => (
        <li key={i} className="flex gap-2 items-start leading-relaxed">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1E5FFF] flex-shrink-0" />
          <div className="flex-1">{renderItem(it)}</div>
        </li>
      ))}
    </ul>
  );
  if (node.type === "ol") return (
    <ol className="my-2 space-y-2">
      {node.items.map((it, i) => (
        <li key={i} className="flex gap-2.5 items-start leading-relaxed">
          <span className="w-6 h-6 rounded-lg gradient-sky text-[#1E5FFF] font-mono-num font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
          <div className="flex-1 pt-0.5">{renderItem(it)}</div>
        </li>
      ))}
    </ol>
  );
  return <p className="my-2 leading-relaxed text-[15px] text-zinc-800" dangerouslySetInnerHTML={{ __html: inline(node.text) }} />;
}

function parse(text) {
  const lines = (text || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let buffer = null; // {type: 'ul'|'ol', items:[]}
  const flushBuffer = () => { if (buffer) { blocks.push(buffer); buffer = null; } };
  const paraLines = [];
  const flushPara = () => {
    if (paraLines.length) {
      blocks.push({ type: "p", text: paraLines.join(" ").trim() });
      paraLines.length = 0;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    // linha em branco: fecha parágrafo, mas mantém lista aberta
    if (!line.trim()) { flushPara(); continue; }
    let m;
    if (/^---+$/.test(line.trim())) { flushPara(); flushBuffer(); blocks.push({ type: "hr" }); continue; }
    if ((m = line.match(/^###\s+(.*)$/))) { flushPara(); flushBuffer(); blocks.push({ type: "h3", text: m[1] }); continue; }
    if ((m = line.match(/^##\s+(.*)$/))) { flushPara(); flushBuffer(); blocks.push({ type: "h2", text: m[1] }); continue; }
    if ((m = line.match(/^#\s+(.*)$/))) { flushPara(); flushBuffer(); blocks.push({ type: "h1", text: m[1] }); continue; }
    if ((m = line.match(/^>\s?(.*)$/))) { flushPara(); flushBuffer(); blocks.push({ type: "quote", text: m[1] }); continue; }
    // Bullet list item (dash/asterisk/bullet). Se indentado E dentro de OL, vira sub-bullet do último item.
    const bulletMatch = raw.match(/^(\s*)[-*•]\s+(.*)$/);
    if (bulletMatch) {
      flushPara();
      const indent = bulletMatch[1].length;
      const text = bulletMatch[2];
      if (buffer && buffer.type === "ol" && indent >= 2 && buffer.items.length) {
        const last = buffer.items[buffer.items.length - 1];
        const obj = typeof last === "string" ? { text: last, sub: [] } : last;
        obj.sub = obj.sub || [];
        obj.sub.push(text);
        buffer.items[buffer.items.length - 1] = obj;
        continue;
      }
      if (buffer && buffer.type === "ul" && indent >= 2 && buffer.items.length) {
        const last = buffer.items[buffer.items.length - 1];
        const obj = typeof last === "string" ? { text: last, sub: [] } : last;
        obj.sub = obj.sub || [];
        obj.sub.push(text);
        buffer.items[buffer.items.length - 1] = obj;
        continue;
      }
      if (!buffer || buffer.type !== "ul") { flushBuffer(); buffer = { type: "ul", items: [] }; }
      buffer.items.push(text);
      continue;
    }
    // Ordered list item
    if ((m = line.match(/^\s*\d+[\.)]\s+(.*)$/))) {
      flushPara();
      if (!buffer || buffer.type !== "ol") { flushBuffer(); buffer = { type: "ol", items: [] }; }
      buffer.items.push(m[1]);
      continue;
    }
    // Linha indentada dentro de uma lista → continuação do último item
    if (buffer && /^\s{2,}/.test(raw) && buffer.items.length) {
      const last = buffer.items[buffer.items.length - 1];
      if (typeof last === "string") {
        buffer.items[buffer.items.length - 1] = last + " " + line.trim();
      } else {
        last.text = last.text + " " + line.trim();
      }
      continue;
    }
    flushBuffer();
    paraLines.push(line);
  }
  flushPara(); flushBuffer();
  return blocks;
}

export default function AIContent({ children, className = "", showBookHint = true }) {
  const text = typeof children === "string" ? children : "";
  const blocks = parse(text);
  const mentionsBook = /livro|Caminhos para Aprovação|cap[ií]tulo/i.test(text);
  return (
    <div className={`ai-content ${className}`}>
      {blocks.map((b, i) => <Block key={i} node={b} />)}
      {showBookHint && mentionsBook && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#B45309] bg-[#FFF6EA] px-2.5 py-1 rounded-full">
          <BookOpen size={11} weight="fill" /> Baseado no livro
        </div>
      )}
    </div>
  );
}
